// §4 — Insurance. Ported from Insurance.jsx; the prototype's loan.settlement.*
// insurance fields are remapped onto the `insurance` jsonb (InsuranceInfo).
// Completeness + Fair-Plan/DIC/HOA logic come from core section4Gate; expiration
// staleness from core insuranceExpiresSoon. P1: NO upload affordances
// (HoaMasterUploadBtn / eFolder peek omitted); the defer ACTION is P2, so the
// deferred banner only renders if the jsonb already carries deferred=true.
import { EncField, EncFlag, Icon, ROField, SumField } from '../atoms';
import { useDocOrderCtx } from '../context';
import { fmtDate, fmtMoney } from '../format';
import { section4Gate, insuranceExpiresSoon, type InsuranceInfo } from '../core';

const EMPTY_INSURANCE: InsuranceInfo = {
  company: null,
  premium: null,
  effective: null,
  expiration: null,
  phone: null,
  email: null,
  hoaMasterCoversWallsIn: false,
  dicExtendedCoverages: false,
  dicCompany: null,
  dicPremium: null,
  dicEffective: null,
  dicExpiration: null,
  dicPhone: null,
  dicEmail: null,
  deferred: false,
  deferReason: null
};

/** Purchase-only rule with no core equivalent: the effective date must land on
 *  or before funding. Presentation validation, not a gate. */
function effectiveAfterFunding(effective: string | null, closingDate: string | null): boolean {
  if (!effective || !closingDate) {
return false;
}
  return new Date(effective) > new Date(closingDate);
}

interface InsuranceProps {
  insurance: InsuranceInfo | null;
  stepNo: number;
}

export function InsuranceSection({ insurance, stepNo }: InsuranceProps) {
  const { editable, loanFacts, closingDate, patchInsurance } = useDocOrderCtx();
  const s = insurance ?? EMPTY_INSURANCE;
  const setIns = (patch: Partial<InsuranceInfo>) => patchInsurance(patch);
  const deferred = !!s.deferred;
  const setDeferred = (next: boolean, reason?: string) =>
    patchInsurance({ deferred: next, deferReason: next ? reason ?? s.deferReason ?? '' : null });

  const isPurchase = /purchase/i.test(loanFacts.purpose || '');
  const gate = section4Gate({ purpose: loanFacts.purpose, propertyType: loanFacts.propertyType }, s);
  const isFairPlan = /fair\s*plan/i.test(s.company || '');
  const needsDic = gate.needsDic;
  const hoaCovers = gate.hoaCovers;

  const effectiveWarn = isPurchase && effectiveAfterFunding(s.effective, closingDate);
  // R4 #21: the <60-day rule applies to purchases AND refis; negative days = expired before funding.
  const expirationWarn = insuranceExpiresSoon(s.expiration, closingDate);
  const dicEffectiveWarn = isPurchase && effectiveAfterFunding(s.dicEffective, closingDate);
  const dicExpirationWarn = insuranceExpiresSoon(s.dicExpiration, closingDate);
  const daysAfterClose =
    closingDate && s.expiration ? Math.round((new Date(s.expiration).getTime() - new Date(closingDate).getTime()) / 86400000) : null;
  const dicDaysAfter =
    closingDate && s.dicExpiration ? Math.round((new Date(s.dicExpiration).getTime() - new Date(closingDate).getTime()) / 86400000) : null;

  const expiryCopy = (days: number | null, suffix = '') =>
    days === null
      ? null
      : days < 0
        ? `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} BEFORE funding — renewal policy required${suffix}.`
        : `Expires ${days} day${days === 1 ? '' : 's'} after funding — renewal policy required${suffix}.`;
  const isCondo = /condo/i.test(loanFacts.propertyType || '');
  const isAttachedCondo = isCondo && !/detached|site/i.test(loanFacts.propertyType || '');
  const insCoLabel = isAttachedCondo ? 'HO-6 Insurance Company' : 'Insurance Company';
  const fieldsEditable = editable && !deferred;

  // Post-submission / read-only viewer summary.
  if (!editable) {
    return (
      <section className="ws-section" id="sec-insurance">
        <div className={`card card-pad snapshot-compact ${deferred ? 'sec-deferred' : ''}`}>
          <div className="ws-section-head">
            <h3 className="ws-section-title">
              <span className={`ws-step-no ${deferred ? 'muted' : ''}`}>{stepNo}</span>Insurance
              {deferred && (
                <span className="defer-pill">
                  <Icon name="clock" size={11} />Deferred · EOI pending
                </span>
              )}
            </h3>
            {!deferred && <EncFlag label="Synced from Encompass" />}
          </div>
          {deferred && (
            <div className="defer-block">
              <div className="defer-row">
                <Icon name="alertCircle" size={14} className="defer-ico" />
                <div className="defer-body">
                  <div className="defer-title">Insurance was deferred at submission.</div>
                  <div className="defer-sub">Docs cannot be balanced or released until the EOI has been received.</div>
                  <div className="defer-reason-row">
                    <span className="defer-reason-lbl">Reason</span>
                    <span className="defer-reason-val">{s.deferReason || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {hoaCovers ? (
            <div className="sum-entity">
              <Icon name="shield" size={12} />
              <span>
                <b>HOA Master Policy</b> covers walls-in with betterments and improvements — no separate hazard policy.
              </span>
            </div>
          ) : (
            <div className="field-grid cols-2 compact">
              <ROField label={insCoLabel} value={s.company} encField={{ id: 'L252', desc: 'Hazard Insurance Company' }} />
              <ROField label="Annual Premium" value={s.premium ? fmtMoney(s.premium) : ''} mono encField={{ id: 230, desc: 'Hazard Insurance Monthly Premium' }} />
              <SumField
                label="Effective Date"
                value={s.effective ? fmtDate(s.effective) : ''}
                mono
                warn={effectiveWarn}
                warnTip="On a purchase, the effective date must be on or before funding."
                sub={effectiveWarn ? 'Effective after funding — policy must be effective on or before the funding date.' : null}
              />
              <SumField
                label="Expiration Date"
                value={s.expiration ? fmtDate(s.expiration) : ''}
                mono
                warn={expirationWarn}
                sub={expirationWarn ? expiryCopy(daysAfterClose) : null}
              />
              <ROField label="Agent Phone" value={s.phone} mono encField={{ id: 'VEND.X163', desc: 'Hazard Insurance Agent Phone' }} />
              <ROField label="Agent Email" value={s.email} mono encField={{ id: 'VEND.X164', desc: 'Hazard Insurance Agent Email' }} />
            </div>
          )}
          {isFairPlan && !hoaCovers &&
            (s.dicExtendedCoverages ? (
              <div className="sum-entity">
                <Icon name="shield" size={12} />
                <span>
                  <b>California Fair Plan</b> — covers all Extended Coverages; no DIC policy required.
                </span>
              </div>
            ) : (
              <>
                <div className="field-subhead">
                  DIC (Differences in Conditions) Policy <span style={{ fontWeight: 400, color: 'var(--fg-subtle)' }}>· secondary required with Fair Plan</span>
                </div>
                <div className="field-grid cols-2 compact">
                  <SumField label="DIC Insurance Company" value={s.dicCompany} />
                  <SumField label="Annual Premium" value={s.dicPremium ? fmtMoney(s.dicPremium) : ''} mono />
                  <SumField
                    label="Effective Date"
                    value={s.dicEffective ? fmtDate(s.dicEffective) : ''}
                    mono
                    warn={dicEffectiveWarn}
                    sub={dicEffectiveWarn ? 'Effective after funding — policy must be effective on or before the funding date.' : null}
                  />
                  <SumField
                    label="Expiration Date"
                    value={s.dicExpiration ? fmtDate(s.dicExpiration) : ''}
                    mono
                    warn={dicExpirationWarn}
                    sub={dicExpirationWarn ? expiryCopy(dicDaysAfter) : null}
                  />
                  <SumField label="Agent Phone" value={s.dicPhone} mono />
                  <SumField label="Agent Email" value={s.dicEmail} mono />
                </div>
              </>
            ))}
        </div>
      </section>
    );
  }

  return (
    <section className="ws-section" id="sec-insurance">
      <div className={`card card-pad snapshot-compact ${deferred ? 'sec-deferred' : ''}`}>
        <div className="ws-section-head">
          <h3 className="ws-section-title">
            <span className={`ws-step-no ${deferred ? 'muted' : ''}`}>{stepNo}</span>Insurance
            {deferred && (
              <span className="defer-pill">
                <Icon name="clock" size={11} />Deferred · cleared for submission
              </span>
            )}
          </h3>
          <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            {!deferred && <EncFlag label="Synced from Encompass" />}
            {editable && deferred && (
              <button type="button" className="link-btn-subtle" onClick={() => setDeferred(false)}>
                <Icon name="rotateCcw" size={11} />Resume Insurance section
              </button>
            )}
          </div>
        </div>

        {deferred && (
          <div className="defer-block">
            <div className="defer-row">
              <Icon name="alertCircle" size={14} className="defer-ico" />
              <div className="defer-body">
                <div className="defer-title">Insurance is deferred for this submission.</div>
                <div className="defer-sub">
                  <b>Doc order can now be submitted.</b> Closing cannot balance or release docs until the EOI has been received. Any updates after
                  submission must be entered in Encompass or coordinated with the Closing team.
                </div>
                <div className="defer-reason-row">
                  <span className="defer-reason-lbl">Reason</span>
                  <span className="defer-reason-val">{s.deferReason || '—'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={deferred ? 'insurance-form-pending' : ''}>
          {isCondo && (
            <div className="hoa-toggle">
              <label className="hoa-toggle-label">
                <input
                  type="checkbox"
                  checked={!!s.hoaMasterCoversWallsIn}
                  disabled={!fieldsEditable}
                  onChange={(e) => setIns({ hoaMasterCoversWallsIn: e.target.checked })}
                />
                <span>
                  <b>HOA Master Policy</b> covers walls-in with betterments and improvements. See full guidelines for additional requirements.
                </span>
              </label>
            </div>
          )}

          {!hoaCovers && (
            <div className="field-grid cols-2 compact">
              <EncField
                label={insCoLabel}
                value={s.company}
                editable={fieldsEditable}
                encField={{ id: 'L252', desc: 'Hazard Insurance Company' }}
                onChange={(v) => setIns({ company: String(v) })}
                placeholder="e.g. State Farm"
              />
              <EncField
                label="Annual Premium"
                value={s.premium}
                editable={fieldsEditable}
                type="number"
                prefix="$"
                encField={{ id: 230, desc: 'Hazard Insurance Monthly Premium' }}
                derived
                derivedNote="annual = Field 230 (monthly) × 12"
                onChange={(v) => setIns({ premium: Number(v) })}
                mono
              />
              <EncField
                label={`Effective Date${isPurchase ? '' : ' (refi — optional)'}`}
                value={s.effective}
                editable={fieldsEditable}
                type="date"
                noSync
                onChange={(v) => setIns({ effective: String(v) })}
                mono
                warn={effectiveWarn}
                warnNote="On a purchase, the effective date must be on or before funding."
              />
              <EncField
                label="Expiration Date"
                value={s.expiration}
                editable={fieldsEditable}
                type="date"
                noSync
                onChange={(v) => setIns({ expiration: String(v) })}
                mono
                warn={expirationWarn}
                warnNote={
                  expirationWarn
                    ? expiryCopy(daysAfterClose, ' (Must be 60+ days out)')
                    : null
                }
              />
              <EncField
                label="Agent Phone"
                value={s.phone}
                editable={fieldsEditable}
                format="phone"
                encField={{ id: 'VEND.X163', desc: 'Hazard Insurance Agent Phone' }}
                onChange={(v) => setIns({ phone: String(v) })}
                mono
                placeholder="555-555-1234"
              />
              <EncField
                label="Agent Email"
                value={s.email}
                editable={fieldsEditable}
                encField={{ id: 'VEND.X164', desc: 'Hazard Insurance Agent Email' }}
                onChange={(v) => setIns({ email: String(v) })}
                mono
              />
            </div>
          )}

          {isFairPlan && !hoaCovers && (
            <div className="dic-block">
              <div className="banner warn compact-banner">
                <Icon name="alertCircle" size={14} className="banner-ico" />
                <span>
                  <b>California Fair Plan</b> policies require a Differences in Conditions (DIC) policy or Fair Plan must cover all Extended Coverages.
                </span>
              </div>
              <label className="dic-toggle">
                <input
                  type="checkbox"
                  checked={!!s.dicExtendedCoverages}
                  disabled={!fieldsEditable}
                  onChange={(e) => setIns({ dicExtendedCoverages: e.target.checked })}
                />
                <span>Fair Plan covers all Extended Coverages — no DIC required</span>
              </label>
              {needsDic && (
                <>
                  <div className="field-subhead">
                    DIC (Differences in Conditions) Policy
                  </div>
                  <div className="field-grid cols-2 compact">
                    <EncField label="DIC Insurance Company" value={s.dicCompany} editable={fieldsEditable} encField={{ id: 'VEND.X64', desc: 'DIC Insurance Company' }} onChange={(v) => setIns({ dicCompany: String(v) })} placeholder="e.g. ICW Group" />
                    <EncField label="Annual Premium" value={s.dicPremium} editable={fieldsEditable} type="number" prefix="$" encField={{ id: '1630', desc: 'DIC Monthly Premium × 12' }} onChange={(v) => setIns({ dicPremium: Number(v) })} mono />
                    <EncField
                      label={`Effective Date${isPurchase ? '' : ' (refi — optional)'}`}
                      value={s.dicEffective}
                      editable={fieldsEditable}
                      type="date"
                      noSync
                      onChange={(v) => setIns({ dicEffective: String(v) })}
                      mono
                      warn={dicEffectiveWarn}
                      warnNote="On a purchase, the effective date must be on or before funding."
                    />
                    <EncField
                      label="Expiration Date"
                      value={s.dicExpiration}
                      editable={fieldsEditable}
                      type="date"
                      noSync
                      onChange={(v) => setIns({ dicExpiration: String(v) })}
                      mono
                      warn={dicExpirationWarn}
                      warnNote={
                        dicExpirationWarn
                          ? expiryCopy(dicDaysAfter, ' (Must be 60+ days out)')
                          : null
                      }
                    />
                    <EncField label="Agent Phone" value={s.dicPhone} editable={fieldsEditable} format="phone" encField={{ id: 'VEND.X71', desc: 'DIC Agent Phone' }} onChange={(v) => setIns({ dicPhone: String(v) })} mono placeholder="555-555-1234" />
                    <EncField label="Agent Email" value={s.dicEmail} editable={fieldsEditable} encField={{ id: 'VEND.X73', desc: 'DIC Agent Email' }} onChange={(v) => setIns({ dicEmail: String(v) })} mono />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
